import { useState, useRef, useEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/use-notifications.js';
import { requestPermission, isPermissionRequested } from '../services/push.js';
import { NotificationItem } from './notification-item.js';
import { loadPrefs } from '../services/notification-prefs.js';
import { pushEnabledHere } from '../services/web-push-client.js';

/** What this browser is set to, in words: which servers, and whether it is notified when aidev is closed */
export function browserState(): string {
  const prefs = loadPrefs();
  const which = prefs.scope === 'all' ? 'All servers' : prefs.servers.length === 0 ? 'No server' : prefs.servers.length === 1 ? '1 server' : `${prefs.servers.length} servers`;
  return `${which}, ${pushEnabledHere() ? 'also when aidev is closed' : 'only while aidev is open'}`;
}

/**
 * The bell and its panel of recent events (ready decisions, digests, alerts). `below`: under the bell, for a header
 * at the top of the page. `side`: beside the sidebar, its bottom on the bell (which sits at the bottom of the screen),
 * rendered at the page level so the sidebar never clips it; a sheet at the bottom of a phone screen.
 */
export function NotificationBell({ placement = 'below' }: { placement?: 'below' | 'side' }) {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const [sidePosition, setSidePosition] = useState<CSSProperties>({});
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on a click outside the bell and the panel (the side panel lives outside the bell's element), or Escape
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        bellRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function handleBellClick() {
    if (placement === 'side' && ref.current) {
      const r = ref.current.getBoundingClientRect();
      const phone = window.innerWidth < 640;
      setSidePosition(phone
        ? { position: 'fixed', left: 8, right: 8, bottom: 8, width: 'auto' }
        : { position: 'fixed', left: r.right + 10, bottom: Math.max(8, window.innerHeight - r.bottom) });
    }
    setOpen((prev) => !prev);
    // Request push notification permission on first interaction with the bell
    if (!isPermissionRequested()) {
      requestPermission();
    }
  }

  function handleNotificationClick(id: string, link: string) {
    markRead(id);
    setOpen(false);
    navigate(link);
  }

  const panel = (
    <div ref={panelRef} role="dialog" aria-label="Notifications" tabIndex={-1}
      className={`animate-scale-in z-50 flex w-[22rem] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg border shadow-lg focus:outline-none ${placement === 'below' ? 'absolute right-0 mt-2 max-h-[28rem]' : 'max-h-[calc(100vh-1.5rem)]'}`}
      style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface)', ...(placement === 'side' ? sidePosition : {}) }}>
      <div className="flex items-baseline justify-between gap-3 px-4 pb-2 pt-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          Notifications
          {unreadCount > 0 && <span className="ml-2 text-xs font-normal" style={{ color: 'var(--color-text-3)' }}>{unreadCount} new</span>}
        </h2>
        {unreadCount > 0 && (
          <button type="button" onClick={markAllRead} className="text-xs hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
            Mark all read
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <p className="px-4 py-6 text-sm" style={{ color: 'var(--color-text-3)' }}>
            Nothing new. Ready decisions and alerts from your servers show up here.
          </p>
        ) : (
          <ul>
            {notifications.map((n, i) => (
              <li key={n.id} style={{ borderTop: i ? '1px solid var(--color-border)' : undefined }}>
                <NotificationItem notification={n} onClick={handleNotificationClick} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <Link to="/settings#browser-notifications" onClick={() => setOpen(false)}
        className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--color-surface-2)] focus-visible:bg-[var(--color-surface-2)] focus-visible:outline-none"
        style={{ borderTop: '1px solid var(--color-border-2)' }}>
        <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" style={{ color: 'var(--color-brand-light)' }} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </svg>
        <span className="min-w-0">
          <span className="block text-sm" style={{ color: 'var(--color-brand-light)' }}>Notifications on this browser</span>
          <span className="block text-xs" style={{ color: 'var(--color-text-3)' }}>{browserState()}</span>
        </span>
      </Link>
    </div>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        ref={bellRef}
        type="button"
        onClick={handleBellClick}
        className="relative rounded-md p-2 focus-visible:outline focus-visible:outline-2"
        style={{ color: open ? 'var(--color-text)' : 'var(--color-text-3)' }}
        aria-label={unreadCount ? `Notifications, ${unreadCount} new` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--color-error)' }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (placement === 'side' ? createPortal(panel, document.body) : panel)}
    </div>
  );
}
